/**
 * The console's navigation model — one entry per page, grouped into sections, each entry
 * gated by a permission.
 *
 * ## Why sections
 *
 * Twenty-one pages in one flat list is not a menu, it is an inventory. An operator looking
 * for the deposit minimum had to read every label to find out that it was not called
 * "deposits", and adjacent entries had nothing to do with each other — Live rates sat above
 * Users, App banners below Push. Grouping does not remove a single page or change a single
 * path; it means the eye can discard four sections at a glance and read one.
 *
 * The sections are named for what an operator came to do, not for how the code is arranged:
 * a support agent chasing a ticket, a compliance reviewer clearing a queue, and a finance
 * lead moving a rate each have one section to look in.
 *
 * **Every path here is verbatim what it was before the grouping.** Deep links and
 * bookmarks are the thing an operator console accumulates most of, and the ordering of a
 * sidebar is not a reason to break one.
 */
import type { ComponentType } from 'react';

import type { AdminPermission } from '../contracts/types.js';
import {
  IconCheck,
  IconRefresh,
  IconAlert,
  IconAtSign,
  IconBank,
  IconBanner,
  IconBell,
  IconChat,
  IconDashboard,
  IconDocument,
  IconFlag,
  IconGauge,
  IconGift,
  IconImage,
  IconMail,
  IconPalette,
  IconPercent,
  IconProviders,
  IconRails,
  IconReceipt,
  IconSend,
  IconSettings,
  IconShield,
  IconTrend,
  IconUsers,
} from './icons.js';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ComponentType<{ size?: number }>;
  /**
   * Permission required to see the entry. `null` → any authenticated admin; a list → any
   * one of them is enough, for the pages that genuinely serve two grants (gift cards is
   * compliance's review queue and finance's rate catalogue on one screen).
   */
  readonly permission: AdminPermission | readonly AdminPermission[] | null;
  /** When true, matches only the exact path (the dashboard index route). */
  readonly end?: boolean;
}

export interface NavSection {
  /** Stable key for the render, never shown. */
  readonly id: string;
  /**
   * The heading. Empty for the first section: a lone Dashboard entry under the word
   * "Overview" is a label explaining a label.
   */
  readonly label: string;
  readonly items: readonly NavItem[];
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'overview',
    label: '',
    items: [{ to: '/', label: 'Dashboard', icon: IconDashboard, permission: null, end: true }],
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      { to: '/users', label: 'Users', icon: IconUsers, permission: 'users:read' },
      // `end` because the tier ceilings live underneath this path; without it both entries
      // highlight at once and the sidebar stops saying which page you are on.
      { to: '/kyc', label: 'ID checks', icon: IconShield, permission: 'kyc:read', end: true },
      // Directly after the queue, because they are two halves of one question: the review
      // decides which tier a customer reaches, and this decides what that tier is worth.
      { to: '/kyc/limits', label: 'Spending limits', icon: IconGauge, permission: 'kyc:review' },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      { to: '/transactions', label: 'Transactions', icon: IconReceipt, permission: 'transactions:read' },
      { to: '/rates', label: 'Live rates', icon: IconTrend, permission: 'rates:read' },
      // Directly under the rate board, because they are two halves of one question: the board
      // is what the market says, and this is what the house adds to it. Same grant, for the
      // same reason — a fee is a treasury decision, not white-label configuration.
      { to: '/fees', label: 'Fees', icon: IconPercent, permission: 'rates:read' },
      // Beside the rate board rather than under Platform: a deposit minimum is a treasury
      // decision that moves with the coin's naira price, and it is gated on the same grant.
      { to: '/assets/rails', label: 'Deposit networks', icon: IconRails, permission: 'rates:read' },
    ],
  },
  {
    /*
     * The desk gets its own group rather than more entries under Money.
     *
     * Money is what customers did; this is what the house holds and owes because of it. They
     * are read by different people asking different questions, and folding six treasury
     * entries into Money would bury the transaction feed that support staff open all day.
     */
    id: 'treasury',
    label: 'Treasury',
    items: [
      { to: '/treasury', label: 'Overview', icon: IconGauge, permission: 'rates:read', end: true },
      // Directly under the overview, because the count on that page links here and a queue you
      // cannot reach in one hop is a queue that grows.
      { to: '/treasury/review', label: 'Review queue', icon: IconAlert, permission: 'rates:read' },
      { to: '/treasury/orders', label: 'Orders', icon: IconReceipt, permission: 'rates:read' },
      // "Your own trades" said nothing about what the page is for: every page in this group is
      // about trades, and "your own" reads as a filter on the orders above it rather than as a
      // different act entirely. This names the act - you went and bought or sold coin yourself,
      // and this is where you tell your books about it.
      { to: '/treasury/rebalances', label: 'Buying and selling coin', icon: IconRefresh, permission: 'rates:read' },
      { to: '/treasury/reconciliation', label: 'Balance check', icon: IconCheck, permission: 'rates:read' },
      { to: '/treasury/custody-sweep', label: 'Move coin to main account', icon: IconRefresh, permission: 'rates:read' },
      { to: '/treasury/movements', label: 'Stuck movements', icon: IconRefresh, permission: 'rates:write' },
      { to: '/treasury/thresholds', label: 'Trading limits', icon: IconSettings, permission: 'rates:read' },
    ],
  },
  {
    id: 'catalogue',
    label: 'What we sell',
    items: [
      // Two grants on one page — compliance reviews redemptions, finance prices the brands —
      // so either one opens it and each tab inside guards its own half.
      { to: '/giftcards', label: 'Gift cards', icon: IconGift, permission: ['kyc:review', 'rates:write'] },
      { to: '/banks', label: 'Banks', icon: IconBank, permission: 'providers:read' },
      // Beside Banks rather than under Money, because it is the same species of thing: a
      // directory synced from a provider whose artwork and availability are ours.
      //
      // The grant is NOT the one Banks carries, and that is not a taste call. The API gates
      // every bill-service route — the list included — on superadmin, the same way it gates
      // the banners and the brand assets, because the tiles are operator-authored artwork that
      // lands on every customer's Bills tab. This entry used to say `providers:read`, which
      // every role holds, so three roles out of four were shown a link that answered a
      // permission error. An entry that opens an error is worse than no entry.
      { to: '/bills/services', label: 'Bill services', icon: IconReceipt, permission: 'admins:write' },
    ],
  },
  {
    id: 'comms',
    label: 'Content & comms',
    items: [
      { to: '/support', label: 'Support inbox', icon: IconChat, permission: 'users:read' },
      { to: '/support-settings', label: 'Support settings', icon: IconSettings, permission: 'admins:write' },
      { to: '/email-templates', label: 'Email templates', icon: IconMail, permission: 'admins:write' },
      // Directly under the templates, because the three are one job: write the message, decide
      // what it looks like and where it leaves from, then send one.
      { to: '/email-settings', label: 'Email settings', icon: IconAtSign, permission: 'admins:write' },
      { to: '/email-broadcast', label: 'Email broadcast', icon: IconSend, permission: 'admins:write' },
      { to: '/push', label: 'Push broadcast', icon: IconBell, permission: 'admins:write' },
      { to: '/banners', label: 'App banners', icon: IconBanner, permission: 'admins:write' },
      { to: '/legal', label: 'Legal & about', icon: IconDocument, permission: 'admins:write' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    items: [
      // `end` because Provider keys lives underneath this path — without it both entries
      // highlight at once and the sidebar stops telling you which page you are on.
      { to: '/providers', label: 'Providers', icon: IconProviders, permission: 'providers:read', end: true },
      // Sits next to Providers because they are two halves of one question — routing order
      // there, vendor activation here — and an operator chasing "why is this rail dead?"
      // needs both within one glance of each other.
      { to: '/providers/credentials', label: 'Provider logins', icon: IconShield, permission: 'providers:read' },
      { to: '/flags', label: 'Feature flags', icon: IconFlag, permission: 'flags:read' },
      { to: '/settings/business', label: 'Business settings', icon: IconPalette, permission: 'admins:write' },
      { to: '/brand', label: 'Brand assets', icon: IconImage, permission: 'admins:write' },
      // DISPOSABLE: a one-off Busha diagnostic. Remove this entry with the page.
    ],
  },
];

/**
 * Every entry, flattened.
 *
 * Kept because the sections are a *presentation* of the same list, and anything asking "is
 * this path a page?" wants the list rather than the grouping.
 */
export const NAV_ITEMS: readonly NavItem[] = NAV_SECTIONS.flatMap((section) => section.items);

/**
 * What the topbar says above each page.
 *
 * Keyed by exact path, and a path missing from here falls back to the platform's own name —
 * which reads as "this page has no title" rather than as a deliberate choice. The whole
 * Treasury section was missing for exactly that reason: the six desk screens were added to
 * the sidebar and nothing was added here, so an operator on the review queue saw the brand
 * name where the page name belongs.
 *
 * The two order screens are one path with an id on the end, so the detail view keeps the
 * list's title; it prints the order's own reference as its heading anyway.
 */
export const PAGE_TITLES: Readonly<Record<string, string>> = {
  '/': 'Dashboard',
  '/flags': 'Feature flags',
  '/providers': 'Payment routing',
  '/providers/credentials': 'Provider logins',
  '/banks': 'Banks',
  '/bills/services': 'Bill services',
  '/rates': 'Live rates',
  '/fees': 'Fees',
  '/assets/rails': 'Deposit networks',
  '/treasury': 'Treasury',
  '/treasury/review': 'Review queue',
  '/treasury/orders': 'Treasury orders',
  '/treasury/rebalances': 'Buying and selling coin',
  '/treasury/reconciliation': 'Balance check',
  '/treasury/custody-sweep': 'Move coin to main account',
  '/treasury/movements': 'Stuck movements',
  '/treasury/thresholds': 'Trading limits',
  '/users': 'Users',
  '/kyc': 'ID checks',
  '/kyc/limits': 'Spending limits',
  '/giftcards': 'Gift cards',
  '/transactions': 'Transactions',
  '/support': 'Support inbox',
  '/settings/business': 'Business settings',
  '/email-templates': 'Email templates',
  '/email-settings': 'Email settings',
  '/email-broadcast': 'Email broadcast',
  '/legal': 'Legal & about',
  '/push': 'Push broadcast',
  '/banners': 'App banners',
  '/support-settings': 'Support settings',
  '/brand': 'Brand assets',
  // DISPOSABLE, with the page.
};
